(ns dashboard.views.analysis.structured-analysis.browse-structured-analyses
  (:require ["react" :as react]
            [dashboard.components.paginated-list.view :as paginated-list]
            [dashboard.entity.analysis.profile :as analysis.profile]
            [dashboard.entity.analysis.search :as analysis.search]
            [dashboard.notifications :as notifications]
            [dashboard.services.http-config :as http-config]
            [dashboard.subs :as common-subs]
            [dashboard.ui :as ui]
            [dashboard.utils :as utils]
            [dashboard.views.analysis.common :as analysis]
            [io.repliance.util.tree :as tree]
            [re-frame.core :as rf]))

(def view-config-path [::view])

(rf/reg-sub
 ::latest-structured-analyses
 (fn [db _]
   (map #_(comp first second)
    (fn [[_ structured-analyses]]
      (let [latest (first structured-analyses)]
        (assoc latest :paginated-list/row-key (:construct-id latest))))
        (get-in db [:structured-analyses :data]))))

(rf/reg-sub
 ::sending?
 (fn [db _]
   (get-in db (conj view-config-path :sending))))

(rf/reg-event-fx
 ::set-structured-analysis-for-question
 (fn [{:keys [db]} [_ question-id selection state-fn]]
   (when state-fn
     (state-fn :start))
   {:db (assoc-in db (conj view-config-path :sending) true)
    :http-xhrio {:method :post
                 :uri (str "/api/structured-analysis/questions/" question-id)
                 :params selection
                 :timeout http-config/timeout
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success [::set-structured-analysis-for-question-success question-id state-fn]
                 :on-failure [::set-structured-analysis-for-question-failed state-fn]}}))

(rf/reg-event-fx
 ::set-structured-analysis-for-question-success
 (fn [{:keys [db]} [_ question-id state-fn structured-analysis-questions]]
   (when state-fn
     (state-fn :complete))
   {:db (-> db
            (assoc-in (conj view-config-path :sending) false)
            (assoc-in [:structured-analysis-questions :data question-id] structured-analysis-questions))
    :fx [[:dispatch [::notifications/add {:message "Linked analyses updated"}]]
         [:dispatch [::ui/pop-modal]]]}))

(rf/reg-event-fx
 ::set-structured-analysis-for-question-failed
 (fn [{:keys [db]} [_ state-fn]]
   (when state-fn
     (state-fn :complete))
   {:db (assoc-in db (conj view-config-path :sending) false)
    :dispatch [::notifications/add {:message "Failed to link analysis to question"
                                    :class "is-danger"}]}))

(defn make-columns [hidden-roots set-hidden-roots selected-ids set-selected-ids]
  [{:label ""
    :id :actions
    :render (fn [{:keys [construct-id]}]
              (let [selected? (contains? selected-ids construct-id)]
                [:input
                 {:type "checkbox"
                  :checked selected?
                  :on-change #(set-selected-ids (if selected?
                                                  (disj selected-ids construct-id)
                                                  (conj selected-ids construct-id)))}]))}
   analysis/construct-id-column
   {:label "Construct"
    :id :construct
    :sort-key analysis/construct-sort-key
    :render (fn [row]
              [:f> analysis/ConstructColumn row hidden-roots set-hidden-roots])}
   {:label "Category"
    :id :category
    :sort-key (fn [{:keys [categories]}]
                (mapv :name categories))
    :render (fn [{:keys [categories]}]
              [:div.columns
               (map (fn [{:keys [id name]}]
                      [:span.tag.ml-1 {:key (str "category-" id)}
                       name])
                    categories)])}])

(defn make-columns-similarity [selected-ids set-selected-ids]
  (conj (make-columns nil identity selected-ids set-selected-ids)
        {:label "Similarity"
         :id :similarity
         :sort-key :similarity
         :render :similarity}))

(def debouncer (utils/debounce
                (fn [text]
                  (analysis.search/perform-search
                   {:mode :semantic
                    :path [:structured-analysis-semantic-search text]}
                   text))
                740))

(defn StructuredAnalyses [{{:keys [question initial-selection]} :ctx}]
  (let [{:keys [id content display-name section-name]} question
        [selected-ids set-selected-ids] (react/useState initial-selection)
        sending? @(rf/subscribe [::sending?])
        items @(rf/subscribe [::latest-structured-analyses])
        user-preferences @(rf/subscribe [::common-subs/user-preferences])
        [mode set-mode] (react/useState :semantic)
        [query set-query] (react/useState (:content question))
        set-query (fn [text]
                    (set-query text)
                    (when (= mode :semantic)
                     (debouncer text)))
        search-results-data @(analysis.search/get-search-results*
                              [:structured-analysis-semantic-search query])
        search-result-construct-ids (->> search-results-data
                                         :structures
                                         (mapv :construct-id)
                                         set)
        construct-id->similarity (->> search-results-data
                                      :structures
                                      (mapv (juxt :construct-id :similarity))
                                      (into {}))
        roots (set (keep #(when-not (:parent-id %) (:construct-id %))
                         items))
        [hidden-roots set-hidden-roots] (react/useState
                                         (if (:initial-analysis-collapsed-state user-preferences)
                                           roots
                                           #{}))]
    (react/useEffect
     (fn []
       (debouncer query))
     (array nil))
    [:div.card
     [:div.card-header.has-background-primary
      [:title.card-header-title.is-primary "Link structured analyses to question"]
      [:button.card-header-icon {:on-click (fn [_]
                                             (rf/dispatch [::ui/pop-modal]))}
       [:span.icon.is-primary [:i.fas.fa-xmark]]]]
     [:div.card-content.px-0 {:style {:max-height "75vh"
                                      :overflow-y "scroll"}}
      [:div {:style {:width "100%"
                     :height "100%"
                     :overflow-y "scroll"}}
       [:table.m-2
        [:tbody
         [:tr
          [:td [:b.mr-2 "Section:"]]
          [:td section-name]]
         [:tr
          [:td [:b.mr-2 "Question:"]]
          [:td
           [:span display-name]
           [:span.ml-2 content]]]]]
       (let [search-extras
             (fn []
               (let [collapsed-roots (= hidden-roots roots)]
                 [:div.is-flex.is-align-items-center
                  [:button.button.ml-2
                   {:on-click #(set-mode :exact)
                    :class (if (= :exact mode) :is-primary :is-secondary)}
                   "Exact"]
                  [:button.button.mr-3
                   {:on-click (fn []
                                (set-mode :semantic)
                                (debouncer query))
                    :class (if (= :semantic mode) :is-primary :is-secondary)}
                   "Semantic"]
                  [:button.button.is-rounded.ml-2
                   {:title (if collapsed-roots
                             "Expand root nodes"
                             "Collapse root nodes")
                    :on-click (fn [_]
                                (if collapsed-roots
                                  (set-hidden-roots #{})
                                  (set-hidden-roots roots)))}
                   (if collapsed-roots
                     [:span.icon>i.fas.fa-solid.fa-expand]
                     [:span.icon>i.fas.fa-solid.fa-compress])]]))]
        (if (= mode :exact)
          [:f> paginated-list/paginated-list
           {:view-config-path view-config-path
            :allow-tree-sort (= mode :exact)
            :tree-sort-toggle-column-id :construct-id
            :default-view-options {:tree-sort (not= mode :semantic)}
            :class ["p-0"]
            :table-class []
            :hide-header? true
            :hide-pagination? true
            :columns (make-columns
                      hidden-roots set-hidden-roots
                      selected-ids set-selected-ids)
            :search-enabled true
            :search-extras search-extras}
           {:items (mapv
                    #(assoc % :similarity (->> (construct-id->similarity (:construct-id %))
                                               (js/parseFloat)
                                               (* 1000)
                                               (js/parseInt)))
                    items)
            :data-transform (fn [items sort-keyfn sort-comp tree-sort]
                              (if tree-sort
                                (tree/sort-hierarchical items sort-keyfn sort-comp :construct-id)
                                (map #(assoc % :tree/show-ancestors true)
                                     (sort-by sort-keyfn sort-comp
                                              (tree/sort-hierarchical items nil nil :construct-id)))))
            :data-filter (fn [search-query {:tree/keys [ancestors]
                                            :keys [construct construct-id]
                                            :as _structured-analysis}]
                           (if (= mode :exact)
                             (and (utils/match-search-query
                                   search-query
                                   (fn [search-query]
                                     (or (utils/str-includes-icase? construct
                                                                    search-query)
                                         (utils/str-includes-icase? (analysis.profile/format-analysis-id construct-id)
                                                                    search-query))))
                                  (if (and (seq hidden-roots)
                                           (some ancestors hidden-roots))
                                    false
                                    true))
                             (contains? search-result-construct-ids construct-id)))
            :default-sort-keyfn (if (= mode :exact)
                                  :construct-id
                                  :similarity)}]
          [:<>
           [:div.level
            [:input.input
             {:value query
              :on-change (fn [e] (set-query e.target.value))}]
            [search-extras]]
           [:f> paginated-list/PaginatedListPresentation
            {:view-config-path nil
             :class nil
             :table-class nil
             :table-style nil
             :columns (make-columns-similarity selected-ids
                                               set-selected-ids)
             :hide-header? true
             :hide-pagination? true
             :column-editors nil
             :pagination {:items (->> items
                                      (mapv
                                       #(assoc % :similarity
                                               (->> (construct-id->similarity (:construct-id %))
                                                    (js/parseFloat)
                                                    (* 1000)
                                                    (js/parseInt))))
                                      (filter #(construct-id->similarity (:construct-id %)))
                                      (sort-by :similarity >))}
             :view-options nil
             :on-save-editable-row nil}]]))]]
     ;; TODO: Solve oveflow problems when viewport height is small
     [:footer.card-footer
      [:div.card-footer-item {:style {:border "none"}}]
      [:button.button.card-footer-item.is-rounded.is-info.my-2
       {:disabled (= initial-selection selected-ids)
        :style {:border "none"}
        :class (when sending?
                 "is-loading")
        :on-click #(let [id->analysis (into {} (map (juxt :construct-id identity)) items)]
                     (rf/dispatch [::set-structured-analysis-for-question
                                   id
                                   (set (map
                                         (fn [construct-id]
                                           {:construct-id construct-id
                                            :structured-analysis-version-uuid (:version-id (id->analysis construct-id))})
                                         selected-ids))]))}
       (str "Set linked analyses"
            " (" (count selected-ids) ")")]
      [:div.card-footer-item {:style {:border "none"}}]]]))

(rf/reg-event-fx
 ::show-modal
 (fn [{:keys [db]} [_ question]]
   {:dispatch [::ui/push-modal
               {:component #'StructuredAnalyses
                :ctx {:question question
                      :initial-selection (->> (get-in db [:structured-analysis-questions
                                                          :data
                                                          (:id question)])
                                              (map :construct-id)
                                              set)}
                :content-props {:style {:min-width "75vw"
                                        :max-height "90vh"
                                        :padding 0}}}]}))
